/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {ActionType, createAction} from 'typesafe-actions';
import {
	PasswordStrengthDTO,
	StartTwoFactorAuthenticationDTO,
	TwoFactorAuthenticationChangePasswordDTO,
	TwoFactorAuthenticationEnterPhoneVerificationCodeDTO
} from '../../generated/iam-client';
import {ConstraintViolationDTO} from '../../model/validation';
import {PhoneVerificationStopReasonDTO} from '../../services/Stomp/TwoFactorAuthenticationPhoneVerificationStoppedDTO';
import PasswordCriteriaDTO from '../../services/Stomp/PasswordCriteriaDTO';

export const loginSceneActivated =
	createAction('LOGIN_SCENE_ACTIVATED')();

export const loginSceneDeactivated =
	createAction('LOGIN_SCENE_DEACTIVATED')();

export const loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration =
	createAction('LOGIN_SCENE_EMAIL_OWNERSHIP_DENIED_FOR_FLEX_SERVICE_WORKER_REGISTRATION')();

export const loginSceneFlexServiceWorkerRegistrationCompleted =
	createAction('LOGIN_SCENE_FLEX_SERVICE_WORKER_REGISTRATION_COMPLETED')();

export const loginSceneFlexServiceWorkerRegistrationFailed =
	createAction('LOGIN_SCENE_FLEX_SERVICE_WORKER_REGISTRATION_FAILED')();

export const loginSceneFlexServiceWorkerRegistrationExpired =
	createAction('LOGIN_SCENE_FLEX_SERVICE_WORKER_REGISTRATION_EXPIRED')();

export const loginSceneRememberMeTokenSubmitted =
	createAction('LOGIN_SCENE_REMEMBER_ME_TOKEN_SUBMITTED')();

export const loginSceneRememberMeTokenNotOk =
	createAction('LOGIN_SCENE_REMEMBER_ME_TOKEN_NOT_OK')();

export const loginSceneRememberMeTokenOk =
	createAction('LOGIN_SCENE_REMEMBER_ME_TOKEN_OK')();

export const loginSceneAccountNameInputChanged =
	createAction('LOGIN_SCENE_ACCOUNT_NAME_INPUT_CHANGED')();

export const loginScenePasswordInputChanged =
	createAction('LOGIN_SCENE_PASSWORD_INPUT_CHANGED')();

export const loginSceneCredentialsSubmitted =
	createAction('LOGIN_SCENE_CREDENTIALS_SUBMITTED')<{ command: StartTwoFactorAuthenticationDTO }>();

export const loginSceneAuthenticationStarted =
	createAction('LOGIN_SCENE_AUTHENTICATION_STARTED')<{ twoFactorAuthenticationId: string }>();

export const loginSceneStartAuthenticationValidationException =
	createAction('LOGIN_SCENE_START_AUTHENTICATION_VALIDATION_EXCEPTION')<{ violations: { [key: string]: ConstraintViolationDTO[] } }>();

export const loginSceneStartAuthenticationUnexpectedError =
	createAction('LOGIN_SCENE_START_AUTHENTICATION_UNEXPECTED_ERROR')();

export const loginSceneAuthenticationBlocked =
	createAction('LOGIN_SCENE_AUTHENTICATION_BLOCKED')();

export const loginSceneAuthenticationInvalidCredentials =
	createAction('LOGIN_SCENE_AUTHENTICATION_INVALID_CREDENTIALS')();

export const loginSceneAuthenticationNotFound =
	createAction('LOGIN_SCENE_AUTHENTICATION_NOT_FOUND')();

export const loginSceneAuthenticationExpired =
	createAction('LOGIN_SCENE_AUTHENTICATION_EXPIRED')();

export const loginSceneAuthenticationAborted =
	createAction('LOGIN_SCENE_AUTHENTICATION_ABORTED')();

export const loginScenePhoneVerificationStarted =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_STARTED')<{ timeRemainingInSeconds: number }>();

export const loginScenePhoneVerificationStartFailed =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_START_FAILED')();

export const loginScenePhoneVerificationStartBlocked =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_START_BLOCKED')();

export const loginScenePhoneVerificationStartBusy =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_START_BUSY')();

export const loginSceneRequestRetryPhoneVerification =
	createAction('LOGIN_SCENE_REQUEST_RETRY_PHONE_VERIFICATION')();

export const loginSceneRetryPhoneVerificationRequested =
	createAction('LOGIN_SCENE_RETRY_PHONE_VERIFICATION_REQUESTED')();

export const loginSceneRetryPhoneVerificationNotFound =
	createAction('LOGIN_SCENE_RETRY_PHONE_VERIFICATION_NOT_FOUND')();

export const loginSceneRetryPhoneVerificationUnexpectedError =
	createAction('LOGIN_SCENE_RETRY_PHONE_VERIFICATION_UNEXPECTED_ERROR')();

export const loginSceneRequestRestartPhoneVerification =
	createAction('LOGIN_SCENE_REQUEST_RESTART_PHONE_VERIFICATION')();

export const loginSceneRestartPhoneVerificationRequested =
	createAction('LOGIN_SCENE_RESTART_PHONE_VERIFICATION_REQUESTED')();

export const loginSceneRestartPhoneVerificationNotFound =
	createAction('LOGIN_SCENE_RESTART_PHONE_VERIFICATION_NOT_FOUND')();

export const loginSceneRestartPhoneVerificationUnexpectedError =
	createAction('LOGIN_SCENE_RESTART_PHONE_VERIFICATION_UNEXPECTED_ERROR')();

export const loginScenePhoneVerificationCodeInputChanged =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_CODE_INPUT_CHANGED')();

export const loginScenePhoneVerificationCodeSubmitted =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_CODE_SUBMITTED')<{ command: TwoFactorAuthenticationEnterPhoneVerificationCodeDTO }>();

export const loginScenePhoneVerificationCodeEntered =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_CODE_ENTERED')();

export const loginSceneEnterPhoneVerificationCodeNotFound =
	createAction('LOGIN_SCENE_ENTER_PHONE_VERIFICATION_CODE_NOT_FOUND')();

export const loginSceneEnterPhoneVerificationCodeValidationException =
	createAction('LOGIN_SCENE_ENTER_PHONE_VERIFICATION_CODE_VALIDATION_EXCEPTION')<{
		violations: { [key: string]: ConstraintViolationDTO[] }
	}>();

export const loginSceneEnterPhoneVerificationCodeUnexpectedError =
	createAction('LOGIN_SCENE_ENTER_PHONE_VERIFICATION_CODE_UNEXPECTED_ERROR')();

export const loginScenePhoneVerificationStopped =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_STOPPED')<{ reason: PhoneVerificationStopReasonDTO, blocked: boolean }>();

export const loginScenePhoneVerificationFailed =
	createAction('LOGIN_SCENE_PHONE_VERIFICATION_FAILED')();

export const loginScenePasswordChangeRequired =
	createAction('LOGIN_SCENE_PASSWORD_CHANGE_REQUIRED')<{ passwordCriteria: PasswordCriteriaDTO }>();

export const loginSceneNewPasswordInputChanged =
	createAction('LOGIN_SCENE_NEW_PASSWORD_INPUT_CHANGE')<{ password: string }>();

export const loginSceneNewPasswordStrengthEvaluated =
	createAction('LOGIN_SCENE_NEW_PASSWORD_STRENGTH_EVALUATED')<{ passwordStrength: PasswordStrengthDTO }>();

export const loginSceneNewEvaluatePasswordStrengthNotFound =
	createAction('LOGIN_SCENE_NEW_EVALUATE_PASSWORD_STRENGTH_NOT_FOUND')();

export const loginSceneNewEvaluatePasswordStrengthUnexpectedError =
	createAction('LOGIN_SCENE_NEW_EVALUATE_PASSWORD_STRENGTH_UNEXPECTED_ERROR')();

export const loginSceneNewPasswordSubmitted =
	createAction('LOGIN_SCENE_NEW_PASSWORD_SUBMITTED')<{ command: TwoFactorAuthenticationChangePasswordDTO }>();

export const loginScenePasswordChanged =
	createAction('LOGIN_SCENE_PASSWORD_CHANGED')();

export const loginSceneChangePasswordNotFound =
	createAction('LOGIN_SCENE_CHANGE_PASSWORD_NOT_FOUND')();

export const loginSceneChangePasswordValidationException =
	createAction('LOGIN_SCENE_CHANGE_PASSWORD_VALIDATION_EXCEPTION')<{ violations: { [key: string]: ConstraintViolationDTO[] } }>();

export const loginSceneChangePasswordUnexpectedError =
	createAction('LOGIN_SCENE_CHANGE_PASSWORD_UNEXPECTED_ERROR')();

export const loginScenePasswordNotChangedWithSamePassword =
	createAction('LOGIN_SCENE_PASSWORD_NOT_CHANGED_WITH_SAME_PASSWORD')();

export const loginScenePasswordNotChangedWithInsufficientStrength =
	createAction('LOGIN_SCENE_PASSWORD_NOT_CHANGED_WITH_INSUFFICIENT_STRENGTH')<{
		evaluatedStrength: PasswordStrengthDTO, passwordCriteria: PasswordCriteriaDTO
	}>();

export const loginSceneResetButtonClicked =
	createAction('LOGIN_SCENE_RESET_BUTTON_CLICKED')();

// noinspection DuplicatedCode
export type ActionTypes =
	ActionType<typeof loginSceneActivated> |
	ActionType<typeof loginSceneDeactivated> |
	ActionType<typeof loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration> |
	ActionType<typeof loginSceneFlexServiceWorkerRegistrationCompleted> |
	ActionType<typeof loginSceneFlexServiceWorkerRegistrationFailed> |
	ActionType<typeof loginSceneFlexServiceWorkerRegistrationExpired> |
	ActionType<typeof loginSceneRememberMeTokenSubmitted> |
	ActionType<typeof loginSceneRememberMeTokenNotOk> |
	ActionType<typeof loginSceneRememberMeTokenOk> |
	ActionType<typeof loginSceneAccountNameInputChanged> |
	ActionType<typeof loginScenePasswordInputChanged> |
	ActionType<typeof loginSceneCredentialsSubmitted> |
	ActionType<typeof loginSceneAuthenticationStarted> |
	ActionType<typeof loginSceneStartAuthenticationValidationException> |
	ActionType<typeof loginSceneStartAuthenticationUnexpectedError> |
	ActionType<typeof loginSceneAuthenticationBlocked> |
	ActionType<typeof loginSceneAuthenticationInvalidCredentials> |
	ActionType<typeof loginSceneAuthenticationNotFound> |
	ActionType<typeof loginSceneAuthenticationExpired> |
	ActionType<typeof loginSceneAuthenticationAborted> |
	ActionType<typeof loginScenePhoneVerificationStarted> |
	ActionType<typeof loginScenePhoneVerificationStartFailed> |
	ActionType<typeof loginScenePhoneVerificationStartBlocked> |
	ActionType<typeof loginScenePhoneVerificationStartBusy> |
	ActionType<typeof loginSceneRequestRetryPhoneVerification> |
	ActionType<typeof loginSceneRetryPhoneVerificationRequested> |
	ActionType<typeof loginSceneRetryPhoneVerificationNotFound> |
	ActionType<typeof loginSceneRetryPhoneVerificationUnexpectedError> |
	ActionType<typeof loginSceneRequestRestartPhoneVerification> |
	ActionType<typeof loginSceneRestartPhoneVerificationRequested> |
	ActionType<typeof loginSceneRestartPhoneVerificationNotFound> |
	ActionType<typeof loginSceneRestartPhoneVerificationUnexpectedError> |
	ActionType<typeof loginScenePhoneVerificationCodeInputChanged> |
	ActionType<typeof loginScenePhoneVerificationCodeSubmitted> |
	ActionType<typeof loginScenePhoneVerificationCodeEntered> |
	ActionType<typeof loginSceneEnterPhoneVerificationCodeNotFound> |
	ActionType<typeof loginSceneEnterPhoneVerificationCodeValidationException> |
	ActionType<typeof loginSceneEnterPhoneVerificationCodeUnexpectedError> |
	ActionType<typeof loginScenePhoneVerificationStopped> |
	ActionType<typeof loginScenePhoneVerificationFailed> |
	ActionType<typeof loginScenePasswordChangeRequired> |
	ActionType<typeof loginSceneNewPasswordInputChanged> |
	ActionType<typeof loginSceneNewPasswordStrengthEvaluated> |
	ActionType<typeof loginSceneNewEvaluatePasswordStrengthNotFound> |
	ActionType<typeof loginSceneNewEvaluatePasswordStrengthUnexpectedError> |
	ActionType<typeof loginSceneNewPasswordSubmitted> |
	ActionType<typeof loginScenePasswordChanged> |
	ActionType<typeof loginSceneChangePasswordNotFound> |
	ActionType<typeof loginSceneChangePasswordValidationException> |
	ActionType<typeof loginSceneChangePasswordUnexpectedError> |
	ActionType<typeof loginScenePasswordNotChangedWithSamePassword> |
	ActionType<typeof loginScenePasswordNotChangedWithInsufficientStrength> |
	ActionType<typeof loginSceneResetButtonClicked>;
